import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState } from 'react';
import {
  Video,
  Wand2,
  Share2,
  Mail,
  BarChart3,
  Radio,
  Activity,
  GitCompareArrows,
  Shuffle,
  FlaskConical,
  Shield,
  Target,
  Globe,
  KeyRound,
  Workflow,
  ListVideo,
  GraduationCap,
  Handshake,
  GitFork,
} from 'lucide-react';
import { NavGroupWithTitle, PlanUsageSection, PlaysManagementModal, SidebarSearch } from './sidebar/index';
import { HelpModal } from './HelpModal';
import { ReplicModal } from '@/components/tools/ReplicModal';
import { FunnelXModal } from '@/components/tools/FunnelXModal';
import { AdSniperModal } from '@/components/tools/AdSniperModal';
import { useNavigate, useLocation } from 'react-router-dom';
import { useCustomAuth } from '@/contexts/CustomAuthContext';
import { t } from '@/utils/i18n';


export function Sidebar() {
  useLanguage();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { user, hasPermission } = useCustomAuth();
  const isMember = user?.nivel === 'member';

  const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);
  const [isPlaysModalOpen, setIsPlaysModalOpen] = useState(false);
  const [isReplicOpen, setIsReplicOpen] = useState(false);
  const [isFunnelXOpen, setIsFunnelXOpen] = useState(false);
  const [isAdSniperOpen, setIsAdSniperOpen] = useState(false);

  const handleOpenPlaysModal = () => {
    setIsPlaysModalOpen(true);
  };

  const canSee = (key: string) => !isMember || hasPermission(key);

  // VÍDEOS
  const videoItems = [
    { icon: Video, label: t('myVideos'), to: "/dashboard", isActive: pathname === "/dashboard", featureKey: 'videos' },
    { icon: Wand2, label: t('personalizar'), to: "/personalizations", isActive: pathname.startsWith("/personalizations"), featureKey: 'personalizacoes' },
    { icon: Share2, label: t('compartilhar'), to: "/shared", isActive: pathname.startsWith("/shared"), featureKey: 'compartilhar' },
  ].filter((item) => canSee(item.featureKey));

  // CONVERSÃO
  const conversaoItems = [
    { icon: Mail, label: t('capturaLeads'), to: "/vsleads", isActive: pathname === "/vsleads", featureKey: 'vsleads' },
    { icon: GitFork, label: translateInterfaceText("VSL Dinâmica"), to: "/vsl-dinamica", isActive: pathname.startsWith("/vsl-dinamica"), featureKey: 'videos' },
  ].filter((item) => canSee(item.featureKey));

  // ANÁLISES
  const analisesItems = [
    { icon: BarChart3, label: t('métricas'), to: "/metrics", isActive: pathname === "/metrics", featureKey: 'metricas' },
    { icon: Radio, label: translateInterfaceText("Tempo Real"), to: "/tempo-real", isActive: pathname === "/tempo-real", featureKey: 'tempo_real' },
    { icon: Activity, label: t('rastreamento'), to: "/tracking", isActive: pathname === "/tracking", featureKey: 'rastreamento' },
    { icon: GitCompareArrows, label: translateInterfaceText("Comparar VSL"), to: "/comparar-vsl", isActive: pathname === "/comparar-vsl", featureKey: 'comparar_vsl' },
  ].filter((item) => canSee(item.featureKey));

  // OTIMIZAÇÃO
  const otimizacaoItems = [
    { icon: Shuffle, label: t('testeAB'), to: "/ab-test", isActive: pathname === "/ab-test" || pathname.startsWith("/ab-test/"), featureKey: 'ab_test' },
    { icon: FlaskConical, label: t('hookTrack'), to: "/hook-track", isActive: pathname.includes("/hook-track"), featureKey: 'hook_track' },
  ].filter((item) => canSee(item.featureKey));

  // PROTEÇÃO
  const protecaoItems = [
    { icon: Shield, label: t('cloaker'), to: "/vsl-cloaker", isActive: pathname === "/vsl-cloaker", featureKey: 'cloaker' },
    { icon: Target, label: t('pixel'), to: "/pixel", isActive: pathname === "/pixel", featureKey: 'pixel' },
    { icon: Globe, label: t('dominios'), to: "/domain-protection", isActive: pathname === "/domain-protection", featureKey: 'dominios' },
  ].filter((item) => canSee(item.featureKey));

  // FERRAMENTAS
  const ferramentasItems = [
    { icon: Workflow, label: 'Criar Sites', to: "#", onClick: (e: React.MouseEvent) => { e.preventDefault(); setIsReplicOpen(true); }, featureKey: 'sites' },
    { icon: ListVideo, label: 'Criar Quiz', to: "#", onClick: (e: React.MouseEvent) => { e.preventDefault(); setIsFunnelXOpen(true); }, featureKey: 'quiz' },
  ].filter((item) => canSee(item.featureKey));

  // APRENDIZADO
  const aprendizadoItems = [
    { icon: GraduationCap, label: translateInterfaceText("Academy"), to: "/academy", isActive: pathname.startsWith("/academy"), featureKey: 'academy' },
    { icon: Handshake, label: t('parceiros'), to: "/partners", isActive: pathname === "/partners", featureKey: 'parceiros' },
  ].filter((item) => canSee(item.featureKey));

  return (
    <aside className="fixed left-[10px] top-[10px] bottom-[10px] w-[248px] z-40 hidden lg:flex flex-col overflow-hidden rounded-[16px] bg-white border border-[#D9E0EA] shadow-[0_4px_18px_rgba(16,24,40,0.04)]">
      <div className="flex items-center gap-2.5 px-5 pt-[20px] pb-[18px] bg-transparent">
        <img
          src="/assets/imgs/logo-azul-e-preta.png"
          alt={translateInterfaceText("HostVSL")}
          className="h-7 cursor-pointer transition-opacity hover:opacity-80"
          onClick={() => { if (pathname !== '/dashboard') navigate('/dashboard'); }}
        />
      </div>

      {!isMember && (
        <SidebarSearch
          onOpenReplic={() => setIsReplicOpen(true)}
          onOpenFunnelX={() => setIsFunnelXOpen(true)}
        />
      )}

      <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-3 pt-2 pb-6 sidebar-scrollbar flex flex-col gap-[18px]">
        <NavGroupWithTitle title={translateInterfaceText("Vídeos")} items={videoItems} className="!mt-0" />
        {conversaoItems.length > 0 && <NavGroupWithTitle title={translateInterfaceText("Conversão")} items={conversaoItems} />}
        {analisesItems.length > 0 && <NavGroupWithTitle title={translateInterfaceText("Análises")} items={analisesItems} />}
        {otimizacaoItems.length > 0 && <NavGroupWithTitle title={translateInterfaceText("Otimização")} items={otimizacaoItems} />}
        {protecaoItems.length > 0 && <NavGroupWithTitle title={translateInterfaceText("Proteção")} items={protecaoItems} />}
        {ferramentasItems.length > 0 && <NavGroupWithTitle title={translateInterfaceText("Ferramentas")} items={ferramentasItems} />}
        {aprendizadoItems.length > 0 && <NavGroupWithTitle title={translateInterfaceText("Aprendizado")} items={aprendizadoItems} />}
      </div>

      <div className="flex-shrink-0 h-px bg-[#DCE3ED]" />

      {!isMember && (
        <div className="flex-shrink-0 bg-white px-3 py-3">
          <PlanUsageSection onAddPlaysClick={handleOpenPlaysModal} />
        </div>
      )}

      <HelpModal open={isHelpModalOpen} onOpenChange={setIsHelpModalOpen} />
      <PlaysManagementModal open={isPlaysModalOpen} onOpenChange={setIsPlaysModalOpen} />
      <ReplicModal open={isReplicOpen} onOpenChange={setIsReplicOpen} />
      <FunnelXModal open={isFunnelXOpen} onOpenChange={setIsFunnelXOpen} />
      <AdSniperModal open={isAdSniperOpen} onOpenChange={setIsAdSniperOpen} />
    </aside>
  );
}
